import React from 'react';
import i18next from 'i18next';
import { ReactComponent as NotExecuted } from '../../assets/images/svg/notExecuted.svg';
import { ReactComponent as ExecutedIcon } from '../../assets/images/svg/executed.svg';
import { separateByUppercase } from './stringHelpers';

export const ORDER_TYPES = {
  BUY_FOR_DELIVERY: 'BuyForDelivery',
  BUY_FOR_STORAGE: 'BuyForStorage',
  BUY_FOR_IRA: 'BuyForIra',
  SELL_FROM_STORAGE: 'SellFromStorage',
  SELL_FROM_PP: 'SellFromPP',
  SELL_FROM_IRA: 'SellFromIra',
  DELIVERY_FROM_STORAGE: 'DeliverFromStorage',
  CLIENT_OFFSET_BUY_ORDER: 'ClientOffsetBuyOrder',
};

export const ORDER_TYPES_LABELS = {
  [ORDER_TYPES.BUY_FOR_DELIVERY]: i18next.t(
    'account.legacyOrderHistory.orderTypeLabels.buyForDelivery',
  ),
  [ORDER_TYPES.BUY_FOR_STORAGE]: i18next.t(
    'account.legacyOrderHistory.orderTypeLabels.buyForStorage',
  ),
  [ORDER_TYPES.SELL_FROM_STORAGE]: i18next.t(
    'account.legacyOrderHistory.orderTypeLabels.sellFromStorage',
  ),
  [ORDER_TYPES.SELL_FROM_PP]: i18next.t(
    'account.legacyOrderHistory.orderTypeLabels.sellFromPP',
  ),
  [ORDER_TYPES.DELIVERY_FROM_STORAGE]: i18next.t(
    'account.legacyOrderHistory.orderTypeLabels.deliveryFromStorage',
  ),
  [ORDER_TYPES.BUY_FOR_IRA]: i18next.t(
    'account.legacyOrderHistory.orderTypeLabels.buyForIRA',
  ),
  [ORDER_TYPES.SELL_FROM_IRA]: i18next.t(
    'account.legacyOrderHistory.orderTypeLabels.sellFromIRA',
  ),
  [ORDER_TYPES.CLIENT_OFFSET_BUY_ORDER]: i18next.t(
    'account.legacyOrderHistory.orderTypeLabels.clientOffsetBuyOrder',
  ),
};

export const ORDER_STATUS_TYPE = {
  COMPLETED: 'Completed',
  CANCELED: 'Canceled',
  DECLINED: 'Declined',
  PAYMENT_ISSUED: 'PaymentIssued',
  FULL_TRACKING_RECEIVED: 'FullTrackingReceived',
};

export const ORDER_STATUS_TYPE_PICKER = {
  COMPLETED: 'Completed',
  CANCELED: 'Canceled',
  DECLINED: 'Declined',
};

export const orderStatusFilterMatch = (orderType, selectedOrderType) => {
  switch (selectedOrderType) {
    case ORDER_STATUS_TYPE_PICKER.COMPLETED:
      return (
        orderType === ORDER_STATUS_TYPE.COMPLETED ||
        orderType === ORDER_STATUS_TYPE.PAYMENT_ISSUED ||
        orderType === ORDER_STATUS_TYPE.FULL_TRACKING_RECEIVED
      );
    case ORDER_STATUS_TYPE_PICKER.CANCELED:
      return orderType === ORDER_STATUS_TYPE.CANCELED;
    case ORDER_STATUS_TYPE_PICKER.DECLINED:
      return orderType === ORDER_STATUS_TYPE.DECLINED;
    default:
      break;
  }
};

export const ORDER_STATUS = {
  [ORDER_STATUS_TYPE.COMPLETED]: i18next.t(
    'account.legacyOrderHistory.orderStatusTypes.completed',
  ),
  [ORDER_STATUS_TYPE.CANCELED]: i18next.t(
    'account.legacyOrderHistory.orderStatusTypes.canceled',
  ),
  [ORDER_STATUS_TYPE.DECLINED]: i18next.t(
    'account.legacyOrderHistory.orderStatusTypes.declined',
  ),
  [ORDER_STATUS_TYPE.PAYMENT_ISSUED]: i18next.t(
    'account.legacyOrderHistory.orderStatusTypes.completed',
  ),
  [ORDER_STATUS_TYPE.FULL_TRACKING_RECEIVED]: i18next.t(
    'account.legacyOrderHistory.orderStatusTypes.completed',
  ),
};

export const ORDER_STATUS_TYPES_ICONS = {
  [ORDER_STATUS_TYPE.CANCELED]: <NotExecuted />,
  [ORDER_STATUS_TYPE.DECLINED]: <NotExecuted />,
  [ORDER_STATUS_TYPE.COMPLETED]: <ExecutedIcon />,
  [ORDER_STATUS_TYPE.PAYMENT_ISSUED]: <ExecutedIcon />,
  [ORDER_STATUS_TYPE.FULL_TRACKING_RECEIVED]: <ExecutedIcon />,
};

export const ORDER_TYPES_WITH_DESTINATION = [
  ORDER_TYPES.BUY_FOR_DELIVERY,
  ORDER_TYPES.BUY_FOR_STORAGE,
  ORDER_TYPES.BUY_FOR_IRA,
  ORDER_TYPES.DELIVERY_FROM_STORAGE,
];
export const ORDER_TYPES_WITH_SOURCE = [
  ORDER_TYPES.SELL_FROM_PP,
  ORDER_TYPES.SELL_FROM_STORAGE,
  ORDER_TYPES.SELL_FROM_IRA,
  ORDER_TYPES.DELIVERY_FROM_STORAGE,
];

export const CREATED_ON_FORMAT = 'MMM dd, yyyy';

export const STORAGE_LABEL_FORMAT = ':locationName (:storageType)';
export const ADDRESS_FORMAT =
  ':recipient\n:addressLine\n:city, :stateRegion :postalCode';
export const ADDRESS_FORMAT_BUY_FOR_DELIVERY =
  ':addressLine\n:city, :stateRegion :postalCode';

export const BUY_FOR_DELIVERY_ADRESS_FORMAT =
  ':addressLine\n:city, :stateRegion :postalCode';

const EQUITY_DESTINATION_FORMAT = 'Equity :equityNumber, :locationName';
const EQUITY_SOURCE_FORMAT = 'IRA: Equity :equityNumber';

export const getSourceLabel = (legacyOrder) => {
  switch (legacyOrder.orderType) {
    case ORDER_TYPES.SELL_FROM_PP:
      return 'Mailed from private possession';
    case ORDER_TYPES.SELL_FROM_STORAGE:
      return 'Salt Lake City (allocated)';
    case ORDER_TYPES.SELL_FROM_IRA:
      return EQUITY_SOURCE_FORMAT.replace(
        ':equityNumber',
        legacyOrder.iraAccountNumber,
      );
    case ORDER_TYPES.DELIVERY_FROM_STORAGE:
      return ADDRESS_FORMAT.replace(':recipient', legacyOrder.deliveryName)
        .replace(':addressLine', legacyOrder.addressLine1)
        .replace(':city', legacyOrder.city)
        .replace(':stateRegion', legacyOrder.provinceCaption)
        .replace(':postalCode', legacyOrder.postalCode);
    default:
      return '';
  }
};

export const getDestinationLabel = (legacyOrder) => {
  switch (legacyOrder.orderType) {
    case ORDER_TYPES.BUY_FOR_STORAGE:
      return STORAGE_LABEL_FORMAT.replace(
        ':locationName',
        separateByUppercase(legacyOrder.storageLocation),
      ).replace(':storageType', legacyOrder.storageType.toLowerCase());
    case ORDER_TYPES.BUY_FOR_IRA:
      return EQUITY_DESTINATION_FORMAT.replace(
        ':equityNumber',
        legacyOrder.iraAccountNumber,
      ).replace(':storageType', legacyOrder.storageType.toLowerCase());
    case ORDER_TYPES.DELIVERY_FROM_STORAGE:
      return ADDRESS_FORMAT.replace(':recipient', legacyOrder.deliveryName)
        .replace(':addressLine', legacyOrder.addressLine1)
        .replace(':city', legacyOrder.city)
        .replace(':stateRegion', legacyOrder.provinceCaption)
        .replace(':postalCode', legacyOrder.postalCode);
    case ORDER_TYPES.BUY_FOR_DELIVERY:
      return ADDRESS_FORMAT_BUY_FOR_DELIVERY.replace(
        ':addressLine',
        legacyOrder.addressLine1,
      )
        .replace(':city', legacyOrder.city)
        .replace(':stateRegion', legacyOrder.provinceCaption)
        .replace(':postalCode', legacyOrder.postalCode);
    default:
      return '';
  }
};

export const totalPrice = (total, vaultShipping, wireFee, orderType) =>
  ORDER_TYPES_WITH_SOURCE.includes(orderType) && (vaultShipping || wireFee)
    ? total - (vaultShipping + wireFee)
    : total;
